import { Form, Formik } from 'formik';
import React, { useRef } from 'react';
import * as Yup from 'yup';
import Divider from '~/components/divider';
import useEventListener from '~/hooks/use-event-listener';
import useAuthPress from '~/view-data/use-auth-press';
import { Container, Type, Contents, Link, LinkContainer, FormContainer, ImageBackground } from '~/views/landing-press/styles';
import Button from '~components/button';
import { InputGroup, InputMask } from '~/components/form';
import { ImNewspaper } from 'react-icons/im';
import { isValidCnpj } from '~/helper';
import { MASK_CNPJ, MASK_PHONE } from '~constants'
import Logo from '~components/logo';

const schema = Yup.object().shape({
  username: Yup.string().required('Informe o usuário'),
  password: Yup.string().required('Informe a senha'),
});

const forgetSchema = Yup.object().shape({
  email: Yup.string().email('Email inválido').required('Email inválido'),
});

const signUpSchema = Yup.object().shape({
  userName: Yup.string().required('Informe o nome'),
  country: Yup.string().required('Informe o país'),
  name: Yup.string().min(2, 'Verifique se a razão social está correto').required('Informe a razão social'),
  tradeName: Yup.string().min(2, 'Verifique se a nome fantasia está correto').required('Informe a nome fantasia'),
  identify: Yup.mixed().test('', 'Informe o CNPJ ou Número de registro', function(value) {
    let parent = this.parent || {};

    if (!parent.country || /^(brasil|brazil)$/i.test(parent.country)) {
      return value && isValidCnpj(value);
    }
    return !!value;
  }),
  email: Yup.string().email('Informe um email válido').required('Informe o Email'),
});

const getSchema = (mode) => {
  if (mode === 'signup') {
    return signUpSchema;
  }
  return mode === 'forget' ? forgetSchema : schema;
};

export default function LandingPress() {
  const { state, actions } = useAuthPress(),
    signinButton = useRef(null),
    signupButton = useRef(null),
    forgetPassButton = useRef(null);

  const onKeyDown = (event) => {
    let btnRef = state.mode === 'signup' ? signupButton : (state.mode === 'login' ? signinButton : forgetPassButton);
    if (event.keyCode === 13 && btnRef && btnRef.current && !btnRef.current.disabled) {
      btnRef.current.click();
    }
  };

  useEventListener({ name: 'keydown', handler: onKeyDown });

  const renderLogin = ({ errors, isValid, touched, values }) => {
    return (
      <>
        <Logo height="120px" />

        <InputGroup
          type='username'
          name='username'
          label='Usuário'
          autoComplete='new-password'
          autoFocus
          value={values.username}
          hideErrorLabel={true}
          hasError={errors.username && touched.username}
          />

        <InputGroup
          type='password'
          name='password'
          label='Senha'
          autoComplete='new-password'
          value={values.password}
          hideErrorLabel={true}
          hasError={errors.password && touched.password}
          />

        <LinkContainer>
          <Link onClick={() => actions.changeMode('forget')}>
            Esqueci minha senha
          </Link>

          <Link align="right" onClick={() => actions.changeMode('signup')}>
            Cadastre-se
          </Link>
        </LinkContainer>

        <Button
          ref={signinButton}
          type='submit'
          disabled={!isValid}
          primary={true}
          >
          Entrar
        </Button>
      </>
    );
  };

  const renderForgetPassword = ({ errors, isValid, touched, values }) => {
    return (
      <>
        <InputGroup
          type='email'
          name='email'
          label='Email'
          autoFocus
          autoComplete='new-password'
          value={values.email}
          hideErrorLabel={true}
          hasError={errors.email && touched.email}
        />

        <LinkContainer>
          <Link onClick={() => actions.changeMode('login')}>
            Voltar para o Login
          </Link>

          <Link align="right" onClick={() => actions.changeMode('signup')}>
            Cadastre-se
          </Link>
        </LinkContainer>

        <Button
          ref={forgetPassButton}
          type='submit'
          disabled={!isValid}
          primary={true}
        >
          Redefinir senha
        </Button>
      </>
    );
  };

  const renderSignUp = ({ errors, isValid, touched, values, setFieldTouched }) => {
    return (
      <>
        <Divider label="Dados da empresa" />

        <InputGroup
          type="text"
          name="country"
          maxLength={100}
          label="País"
          required={true}
          hideErrorLabel={true}
          hasError={errors.country && touched.country} />

        { (!values.country || /^(brasil|brazil)$/i.test(values.country)) ?
          <InputMask
            mask={MASK_CNPJ}
            name="identify"
            label={'CNPJ'}
            autoFocus
            required={true}
            hideErrorLabel={true}
            hasError={errors.identify && touched.identify}
            />
          :
          <InputGroup
            type="text"
            maxLength={20}
            name="identify"
            required={true}
            label={'Número de Registro'}
            hideErrorLabel={true}
            hasError={errors.identify && touched.identify} />
        }

        <InputGroup
          type="text"
          name="name"
          maxLength={100}
          required={true}
          label="Razão social"
          hideErrorLabel={true}
          hasError={errors.name && touched.name} />
        
        <InputGroup
          type="text"
          name="tradeName"
          required={true}
          label="Nome fantasia"
          maxLength={100}
          hideErrorLabel={true}
          hasError={errors.tradeName && touched.tradeName} />

        <Divider label="Dados do responsável" />

        <InputGroup
          type="text"
          name="userName"
          maxLength={100}
          label="Nome"
          required={true}
          hideErrorLabel={true}
          hasError={errors.userName && touched.userName} />

        <InputGroup
          type="email"
          name="email"
          label="Email"
          required={true}
          maxLength={100}
          hideErrorLabel={true}
          hasError={errors.email && touched.email} />

        <InputMask
          name="phone"
          label="Telefone"
          mask={MASK_PHONE}
          value={values.phone}
          hideErrorLabel={true}
          hasError={errors.phone && touched.phone} />

        <LinkContainer>
          <Link onClick={() => actions.changeMode('login')}>
            Voltar para o Login
          </Link>

          <Link align="right" onClick={() => actions.changeMode('forget')}>
            Esqueci minha senha
          </Link>
        </LinkContainer>

        <Button
          ref={signupButton}
          type='submit'
          disabled={!isValid}
          primary={true}
          >
          Cadastrar
        </Button>
      </>
    );
  };

  return (
    <Container>
      <ImageBackground />
      <Type><ImNewspaper /> Imprensa</Type>
      <Contents>
        <Formik
          validateOnMount={true}
          enableReinitialize={true}
          initialValues={state.loginData || {}}
          validationSchema={getSchema(state.mode)}
          onSubmit={state.mode === 'signup' ? actions.signUpSubmit : (state.mode === 'forget' ? actions.forgetPasswordSubmit : actions.loginSubmit)}
          initialTouched={state.mode === 'signup' ? {} : (state.mode === 'forget' ? { email: true } : { username: true })}
          >
          {(formArgs) => (
            <FormContainer>
              <Form>
                {(!state.mode || state.mode === 'login') && renderLogin(formArgs)}
                {state.mode === 'forget' && renderForgetPassword(formArgs)}
                {state.mode === 'signup' && renderSignUp(formArgs)}
              </Form>
            </FormContainer>
          )}
        </Formik>
      </Contents>
    </Container>
  );
}
